// Windows エクスプローラーや Finder からのドラッグ&ドロップで画像 / 動画ファイルを
// キャンバスに配置する。配置位置は現在表示中の中央。

import { useEffect, type RefObject } from "react";
import { useCanvasStore } from "../store/canvas-store";
import { screenToStage } from "../../../lib/canvas-coordinates";

interface Handlers {
  readonly onDropImage: (file: File, position: { x: number; y: number }) => void;
  readonly onDropVideo: (file: File, position: { x: number; y: number }) => void;
}

export function useDropHandler(
  containerRef: RefObject<HTMLElement | null>,
  handlers: Handlers
): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const onDragOver = (event: DragEvent): void => {
      // ファイルがドラッグされている場合のみ受け入れ可能とする
      if (event.dataTransfer?.types.includes("Files")) {
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      }
    };

    const onDrop = (event: DragEvent): void => {
      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return;
      event.preventDefault();
      const view = useCanvasStore.getState().view;
      // 表示中の中央 = window 中央を stage 座標へ変換
      const stageCenter = screenToStage(
        { x: window.innerWidth / 2, y: window.innerHeight / 2 },
        view
      );
      const offsetStep = 24;
      Array.from(files).forEach((file, index) => {
        const position = {
          x: stageCenter.x + offsetStep * index,
          y: stageCenter.y + offsetStep * index,
        };
        if (file.type.startsWith("image/")) {
          handlers.onDropImage(file, position);
        } else if (file.type.startsWith("video/")) {
          handlers.onDropVideo(file, position);
        }
      });
    };

    container.addEventListener("dragover", onDragOver);
    container.addEventListener("drop", onDrop);
    return () => {
      container.removeEventListener("dragover", onDragOver);
      container.removeEventListener("drop", onDrop);
    };
  }, [containerRef, handlers]);
}
